"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { DocsLayout } from "@/components/cli/DocsLayout";
import { DocsBreadcrumb } from "@/components/cli/DocsBreadcrumb";
import { DocsTableOfContents, type TocItem } from "@/components/cli/DocsTableOfContents";
import { CodeBlock } from "@/components/cli/CodeBlock";
import type { DocsSidebarItem } from "@/components/cli/DocsSidebar";

// Sidebar navigation
const sidebarItems: DocsSidebarItem[] = [
  {
    title: "Get started",
    defaultOpen: true,
    items: [
      { title: "Installation", href: "/cli" },
      { title: "Authentication", href: "/cli/authentication" },
    ],
  },
  {
    title: "Usage",
    defaultOpen: true,
    items: [
      { title: "Claude Code", href: "/cli/usage" },
      { title: "Cursor", href: "/cli/usage/cursor", disabled: true },
      { title: "Kiro", href: "/cli/usage/kiro", disabled: true },
      { title: "Gemini", href: "/cli/usage/gemini", disabled: true },
      { title: "Copilot", href: "/cli/usage/copilot", disabled: true },
    ],
  },
];

// Table of contents items
const tocItems: TocItem[] = [
  { id: "install", title: "Install CLI" },
  { id: "initialize", title: "Initialize project" },
  { id: "new-project", title: "New project" },
  { id: "whats-next", title: "What's next?" },
];

export default function DocsPage() {
  const [activeTocId, setActiveTocId] = useState<string>(tocItems[0]?.id);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;

      let currentSection = tocItems[0]?.id;

      for (const item of tocItems) {
        const element = document.getElementById(item.id);
        if (element) {
          const { offsetTop } = element;
          if (scrollPosition >= offsetTop) {
            currentSection = item.id;
          }
        }
      }

      setActiveTocId(currentSection);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <DocsLayout
      sidebar={sidebarItems}
      rightSidebar={
        <DocsTableOfContents
          items={tocItems}
          activeId={activeTocId}
          onItemClick={setActiveTocId}
        />
      }
    >
      {/* Breadcrumb */}
      <DocsBreadcrumb
        items={[
          { label: "CLI", href: "/cli" },
          { label: "Get started" },
          { label: "Installation" },
        ]}
        className="mb-6"
      />

      {/* Page Title */}
      <h1 className="text-3xl md:text-4xl font-bold text-text mb-4">
        Installation
      </h1>

      {/* Introduction */}
      <p className="text-lg text-text-secondary mb-8 leading-relaxed">
        Install the Specledger CLI and initialize it in your project directory.
        Works on macOS, Linux, and WSL.
      </p>

      {/* Install CLI */}
      <section id="install" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">Install CLI</h2>
        <p className="text-text-secondary mb-6">
          Run the following command to install the Specledger CLI:
        </p>

        <CodeBlock code="curl -fsSL https://specledger.io/install.sh | bash" />

        <div className="mt-6 p-4 bg-bg-alt rounded-xl">
          <p className="text-sm font-medium text-text mb-2">Tips</p>
          <ul className="space-y-1.5 text-sm text-text-muted">
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Works on macOS, Linux, and WSL
            </li>
          </ul>
        </div>
      </section>

      {/* Initialize project */}
      <section id="initialize" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">Initialize project</h2>
        <p className="text-text-secondary mb-6">
          Navigate to your project directory and initialize Specledger:
        </p>

        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-text-secondary mb-2">1. Go to your project</p>
            <CodeBlock code="cd your-project" />
          </div>
          <div>
            <p className="text-sm font-medium text-text-secondary mb-2">2. Initialize project</p>
            <CodeBlock code="sl init" />
          </div>
        </div>

        <div className="mt-6 p-4 bg-bg-alt rounded-xl">
          <p className="text-sm font-medium text-text mb-2">Tips</p>
          <ul className="space-y-1.5 text-sm text-text-muted">
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              This creates a .specledger directory in your project
            </li>
          </ul>
        </div>
      </section>

      {/* New project */}
      <section id="new-project" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">New project</h2>
        <p className="text-text-secondary mb-6">
          Alternatively, create a new project from scratch with Specledger already configured:
        </p>

        <CodeBlock code="sl new" />

        <div className="mt-6 p-4 bg-bg-alt rounded-xl">
          <p className="text-sm font-medium text-text mb-2">Tips</p>
          <ul className="space-y-1.5 text-sm text-text-muted">
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              You will be prompted for the project name
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">-</span>
              Specledger is automatically initialized
            </li>
          </ul>
        </div>
      </section>

      {/* What's next */}
      <section id="whats-next" className="mb-12">
        <h2 className="text-2xl font-bold text-text mb-4">What&apos;s next?</h2>
        <p className="text-text-secondary mb-6">
          Now that you have the CLI installed, authenticate to access Specledger resources.
        </p>

        <Link
          href="/cli/authentication"
          className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
        >
          Set up authentication
          <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </DocsLayout>
  );
}
